import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTenant } from '../../context/TenantContext'; 
import { 
  getVaultAssets, 
  addVaultAsset, 
  updateVaultAsset, 
  deleteVaultAsset 
} from '../../services/db'; // Removed the buggy incrementAssetDownload helper
import { db, storage } from '../../services/firebase'; // Added db import
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { doc, updateDoc, increment } from 'firebase/firestore'; // Added native Firestore tools
import { EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';

export default function ResourceVault({ isOperatorView = false }) {
  const { currentUser } = useAuth();
  const { tenantId, loading: tenantLoading } = useTenant(); 
  
  // --- ADVANCED SECURITY: STEP-UP AUTHENTICATION (FIREBASE RE-AUTH) ---
  const [isVaultUnlocked, setIsVaultUnlocked] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [authError, setAuthError] = useState('');
  const [isAuthenticating, setIsAuthenticating] = useState(false);

  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgressText, setUploadProgressText] = useState('');

  // ==========================================
  // STATE: CENTRALIZED ASSET REPOSITORY
  // ==========================================
  const [assets, setAssets] = useState([]);

  // UI State
  const [activeCategory, setActiveCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAsset, setEditingAsset] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({ 
    title: '', 
    category: 'Presentations', 
    type: 'PDF',
    targetUrl: '' 
  });

  const categories = ['All', 'Presentations', 'Pricing', 'Sales Tools', 'Training', 'Playbooks', 'Technical Specs'];

  // --- ⚡️ LIVE FIREBASE FETCH (MULTI-TENANT) ⚡️ ---
  const fetchAssets = async () => {
    // Prevent fetching sensitive data until the vault is cryptographically unlocked
    if (tenantLoading || !tenantId || !isVaultUnlocked) return;

    setIsLoading(true);
    try {
      const liveAssets = await getVaultAssets(tenantId);
      
      const formattedAssets = liveAssets.map(asset => {
        let updatedStr = 'Just Now';
        if (asset.updatedAt?.toDate) {
          updatedStr = asset.updatedAt.toDate().toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        }
        return { ...asset, updated: updatedStr };
      });

      setAssets(formattedAssets);
    } catch (error) {
      console.error("Failed to fetch vault assets:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAssets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, tenantLoading, isVaultUnlocked]);

  // --- FIREBASE RE-AUTHENTICATION LOGIC ---
  const verifyPassword = async (e) => {
    e.preventDefault();
    if (!passwordInput || !currentUser) return;
    
    setIsAuthenticating(true);
    setAuthError('');

    try {
      const credential = EmailAuthProvider.credential(currentUser.email, passwordInput);
      await reauthenticateWithCredential(currentUser, credential);
      
      setIsVaultUnlocked(true);
      setPasswordInput('');
      
      // Auto-lock the vault after 15 minutes of inactivity
      setTimeout(() => {
        setIsVaultUnlocked(false);
      }, 15 * 60 * 1000); 

    } catch (error) {
      console.error("Vault Authentication Failed:", error);
      setAuthError('Authentication failed. Please verify your password.');
    } finally {
      setIsAuthenticating(false);
    }
  };

  // ==========================================
  // LOGIC & ACTIONS
  // ==========================================
  const handleSearch = (e) => setSearchQuery(e.target.value);

  const filteredAssets = assets.filter(asset => {
    const matchesSearch = asset.title.toLowerCase().includes(searchQuery.toLowerCase()) || asset.category.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = activeCategory === 'All' || asset.category === activeCategory;
    return matchesSearch && matchesCategory;
  });

  const handleDelete = async (id, storagePath) => {
    if (!tenantId) return;
    
    if (window.confirm("Are you sure you want to delete this asset from the fleet vault?")) {
      try {
        await deleteVaultAsset(tenantId, id, storagePath);
        setAssets(assets.filter(a => a.id !== id));
      } catch (error) {
        console.error("Failed to delete asset:", error);
      }
    }
  };

  const openUploadModal = () => {
    setEditingAsset(null);
    setSelectedFile(null);
    setFormData({ title: '', category: 'Presentations', type: 'PDF', targetUrl: '' });
    setIsModalOpen(true);
  };

  const openEditModal = (asset) => {
    setEditingAsset(asset);
    setSelectedFile(null);
    setFormData({ 
      title: asset.title, 
      category: asset.category, 
      type: asset.type,
      targetUrl: asset.fileUrl || ''
    });
    setIsModalOpen(true);
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const handleModalSubmit = async (e) => {
    e.preventDefault();
    if (!tenantId) {
      alert("System Error: Corporate identity not resolved. Cannot upload asset.");
      return;
    }

    setIsSubmitting(true);
    setUploadProgressText('Processing...');
    
    let icon = 'fa-file';
    let color = 'text-slate-500';
    if (formData.type === 'PDF') { icon = 'fa-file-pdf'; color = 'text-red-500'; }
    if (formData.type === 'Sheet') { icon = 'fa-file-excel'; color = 'text-emerald-600'; }
    if (formData.type === 'Interactive') { icon = 'fa-calculator'; color = 'text-emerald-500'; }
    if (formData.type === 'Video') { icon = 'fa-circle-play'; color = 'text-blue-500'; }

    let finalFileUrl = formData.targetUrl;
    let finalStoragePath = editingAsset ? editingAsset.storagePath : null;

    try {
      // EXPLICIT INLINE STORAGE UPLOAD
      if (selectedFile && (formData.type === 'PDF' || formData.type === 'Sheet')) {
        setUploadProgressText('Uploading file to Storage...');
        
        const fileExtension = selectedFile.name.split('.').pop();
        const cleanFileName = selectedFile.name.replace(/[^a-zA-Z0-9.\-_]/g, '_');
        const storagePath = `tenants/${tenantId}/vault_assets/${Date.now()}_${cleanFileName}`;
        const storageRef = ref(storage, storagePath);
        
        await uploadBytes(storageRef, selectedFile, { contentType: selectedFile.type });
        
        finalFileUrl = await getDownloadURL(storageRef);
        finalStoragePath = storagePath;
      }

      setUploadProgressText('Saving metadata...');
      
      const payload = {
        title: formData.title,
        type: formData.type,
        category: formData.category,
        size: selectedFile ? `${(selectedFile.size / (1024*1024)).toFixed(1)} MB` : (formData.type === 'Interactive' || formData.type === 'Video' ? 'Web Link' : (editingAsset?.size || 'Unknown')),
        icon,
        color,
        fileUrl: finalFileUrl,
        storagePath: finalStoragePath
      };

      if (editingAsset) {
        await updateVaultAsset(tenantId, editingAsset.id, payload);
      } else {
        await addVaultAsset(tenantId, payload);
      }
      
      await fetchAssets();
      setIsModalOpen(false);
    } catch (error) {
      console.error("Failed to save asset:", error);
      alert("Error saving asset. See console.");
    } finally {
      setIsSubmitting(false);
      setUploadProgressText('');
    }
  };

  const handleAssetInteraction = async (asset) => {
    if (!tenantId) return;

    // 1. Optimistic UI update for the download counter
    setAssets(assets.map(a => a.id === asset.id ? { ...a, downloads: (a.downloads || 0) + 1 } : a));
    
    // 2. Background DB Write utilizing Firebase's secure Atomic Increment
    try {
      const assetRef = doc(db, 'clients', tenantId, 'vault_assets', asset.id);
      await updateDoc(assetRef, {
        downloads: increment(1)
      });
    } catch (error) {
      console.error("Failed to log interaction:", error);
    }

    // 3. Open the actual file or URL
    if (asset.fileUrl) {
      window.open(asset.fileUrl, '_blank');
    } else {
      alert("No URL or file attached to this asset.");
    }
  };

  const totalDownloads = assets.reduce((sum, asset) => sum + (asset.downloads || 0), 0);
  const topAsset = assets.length > 0 ? assets.reduce((prev, current) => ((prev.downloads || 0) > (current.downloads || 0)) ? prev : current, assets[0]) : null;

  if (tenantLoading) return <div className="p-12 flex justify-center"><div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin"></div></div>;

  // ==========================================
  // RENDER: ZERO-TRUST VAULT LOCK
  // ==========================================
  if (!isVaultUnlocked) {
    return (
      <div className="h-[calc(100vh-120px)] flex flex-col items-center justify-center animate-in fade-in duration-500">
        <div className="bg-white border border-slate-300 shadow-2xl p-10 max-w-md w-full rounded-sm text-center relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-1 bg-emerald-500"></div>
          
          <div className="w-20 h-20 bg-slate-50 border-2 border-slate-200 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner">
            <i className="fa-solid fa-lock text-3xl text-slate-400"></i>
          </div>
          
          <h2 className="text-xl font-black text-slate-900 uppercase tracking-widest mb-2">Zero-Trust Vault</h2>
          <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-6 leading-relaxed">
            Proprietary assets detected. Step-up authentication required to decrypt fleet resources.
          </p>

          <form onSubmit={verifyPassword} className="space-y-5">
            <div className="text-left">
              <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5">
                Re-Authenticate Session
              </label>
              <div className="relative">
                <i className="fa-solid fa-key absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"></i>
                <input
                  type="password"
                  placeholder="Enter your account password"
                  value={passwordInput}
                  onChange={(e) => {
                    setPasswordInput(e.target.value);
                    setAuthError('');
                  }}
                  className={`w-full pl-10 pr-4 py-3 bg-slate-50 border-2 rounded-sm text-sm font-bold focus:outline-none transition-colors ${
                    authError ? 'border-rose-500 bg-rose-50 text-rose-700' : 'border-slate-300 focus:border-emerald-500 text-slate-900'
                  }`}
                />
              </div>
              {authError && (
                <p className="text-[10px] font-black text-rose-500 uppercase tracking-widest mt-2">
                  <i className="fa-solid fa-triangle-exclamation mr-1"></i> {authError}
                </p>
              )}
            </div>

            <button 
              type="submit" 
              disabled={isAuthenticating || !passwordInput}
              className="w-full bg-slate-900 hover:bg-slate-800 disabled:bg-slate-400 text-white font-black text-[11px] uppercase tracking-widest py-4 transition-colors shadow-sm rounded-sm flex items-center justify-center gap-3"
            >
              {isAuthenticating ? (
                <><i className="fa-solid fa-spinner fa-spin"></i> Verifying...</>
              ) : (
                <><i className="fa-solid fa-unlock"></i> Decrypt & Access Vault</>
              )}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ==========================================
  // VIEW RENDERER: OPERATOR (THE ARMORY)
  // ==========================================
  if (isOperatorView) {
    return (
      <div className="animate-in fade-in duration-500 pb-12 relative h-full">
        <div className="flex justify-between items-end mb-6 border-b border-slate-300 pb-4">
          <div>
            <h1 className="text-2xl font-black text-slate-900 tracking-tight uppercase flex items-center gap-3">
              Resource Vault <i className="fa-solid fa-lock-open text-emerald-500 text-lg"></i>
            </h1>
            <p className="text-slate-500 text-sm mt-1 font-medium">Access your approved pitch decks, pricing sheets, and sales tools.</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="relative">
              <i className="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs"></i>
              <input 
                type="text" 
                value={searchQuery}
                onChange={handleSearch}
                placeholder="SEARCH ASSETS..." 
                className="bg-white border border-slate-300 text-slate-800 text-[10px] font-bold pl-8 pr-4 py-2.5 focus:outline-none focus:border-emerald-500 uppercase tracking-widest w-64"
              />
            </div>
            <button 
              onClick={() => { setIsVaultUnlocked(false); setPasswordInput(''); }}
              className="bg-slate-100 hover:bg-slate-200 border border-slate-300 text-slate-600 text-[9px] font-black uppercase tracking-widest px-4 py-2 transition-colors rounded-sm shadow-sm"
            >
              <i className="fa-solid fa-lock mr-2"></i> Lock Vault
            </button>
          </div>
        </div>

        <div className="flex gap-2 mb-8 overflow-x-auto pb-2 scrollbar-hide">
          {categories.map(cat => (
            <button 
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`whitespace-nowrap px-4 py-2 text-[10px] font-bold uppercase tracking-widest transition-colors border ${
                activeCategory === cat 
                  ? 'bg-slate-900 text-white border-slate-900' 
                  : 'bg-white text-slate-600 border-slate-300 hover:border-slate-500 hover:text-slate-900'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {(isLoading || tenantLoading) && (
            <div className="col-span-full py-12 text-center text-slate-500 text-xs font-bold uppercase tracking-widest">
              <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              Decrypting Vault...
            </div>
          )}

          {!(isLoading || tenantLoading) && filteredAssets.map(asset => (
            <div key={asset.id} className="bg-white border border-slate-200 shadow-sm hover:border-emerald-500 transition-colors group flex flex-col">
              <div className="p-6 flex-1 flex items-start gap-4">
                <div className={`w-12 h-12 bg-slate-50 border border-slate-200 flex items-center justify-center text-2xl ${asset.color} group-hover:scale-110 transition-transform`}>
                  <i className={`fa-solid ${asset.icon}`}></i>
                </div>
                <div>
                  <h3 className="text-sm font-black text-slate-900 group-hover:text-emerald-700 transition-colors leading-tight mb-1">{asset.title}</h3>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{asset.category} • {asset.size}</p>
                </div>
              </div>
              <div className="border-t border-slate-100 bg-slate-50 p-4 flex justify-between items-center">
                <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">Updated: {asset.updated}</span>
                <button 
                  onClick={() => handleAssetInteraction(asset)}
                  className="text-[10px] font-bold text-emerald-600 hover:text-emerald-800 uppercase tracking-widest flex items-center gap-2"
                >
                  {asset.type === 'Interactive' ? 'Launch Tool' : 'Download'} <i className={`fa-solid ${asset.type === 'Interactive' ? 'fa-arrow-up-right-from-square' : 'fa-download'}`}></i>
                </button>
              </div>
            </div>
          ))}
          {!(isLoading || tenantLoading) && filteredAssets.length === 0 && (
            <div className="col-span-full py-12 text-center text-slate-500 text-sm font-bold uppercase tracking-widest">
              No assets found matching your criteria.
            </div>
          )}
        </div>
      </div>
    );
  }

  // ==========================================
  // VIEW RENDERER: FOUNDER (THE CMS)
  // ==========================================
  return (
    <div className="animate-in fade-in duration-500 pb-12 relative h-full">
      <div className="flex justify-between items-end mb-6 border-b border-slate-300 pb-4">
        <div>
          <h1 className="text-2xl font-black text-slate-900 tracking-tight uppercase flex items-center gap-3">
            Asset Management Vault <i className="fa-solid fa-lock-open text-emerald-500 text-lg"></i>
          </h1>
          <p className="text-slate-500 text-sm mt-1 font-medium">Upload, organize, and track collateral usage across your fleet.</p>
        </div>
        <div className="flex items-center gap-3">
          <button 
            onClick={() => { setIsVaultUnlocked(false); setPasswordInput(''); }}
            className="bg-slate-100 hover:bg-slate-200 border border-slate-300 text-slate-600 text-[9px] font-black uppercase tracking-widest px-4 py-2 transition-colors rounded-sm shadow-sm"
          >
            <i className="fa-solid fa-lock mr-2"></i> Lock Vault
          </button>
          <button 
            onClick={openUploadModal}
            className="bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] font-bold py-2.5 px-6 transition-colors uppercase tracking-widest shadow-sm flex items-center gap-2"
          >
            <i className="fa-solid fa-cloud-arrow-up"></i> Upload New Asset
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white border border-slate-300 p-4 shadow-sm">
          <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-1">Total Assets</p>
          <p className="text-2xl font-black text-slate-900">{isLoading || tenantLoading ? '-' : assets.length}</p>
        </div>
        <div className="bg-white border border-slate-300 p-4 shadow-sm">
          <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-1">Fleet Downloads</p>
          <p className="text-2xl font-black text-slate-900">{isLoading || tenantLoading ? '-' : totalDownloads.toLocaleString()}</p>
        </div>
        <div className="col-span-1 md:col-span-2 bg-slate-900 border border-slate-800 p-4 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-[9px] font-bold text-emerald-500 uppercase tracking-widest mb-1">Most Active Asset</p>
            <p className="text-sm font-black text-white">{topAsset ? topAsset.title : 'N/A'}</p>
          </div>
          <div className="text-right">
            <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-1">Downloads</p>
            <p className="text-lg font-black text-white">{topAsset ? (topAsset.downloads || 0) : 0}</p>
          </div>
        </div>
      </div>

      <div className="flex justify-between items-center mb-4">
        <div className="relative flex-1 max-w-md">
          <i className="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs"></i>
          <input 
            type="text" 
            value={searchQuery}
            onChange={handleSearch}
            placeholder="SEARCH VAULT..." 
            className="w-full bg-white border border-slate-300 text-slate-800 text-[10px] font-bold pl-8 pr-4 py-2 focus:outline-none focus:border-emerald-500 uppercase tracking-widest"
          />
        </div>
      </div>

      <div className="bg-white border border-slate-300 shadow-sm">
        <div className="bg-slate-100 border-b border-slate-300 px-4 py-3 grid grid-cols-12 gap-4 items-center">
          <div className="col-span-5 text-[10px] font-bold text-slate-600 uppercase tracking-widest">Asset Title</div>
          <div className="col-span-2 text-[10px] font-bold text-slate-600 uppercase tracking-widest">Category</div>
          <div className="col-span-2 text-[10px] font-bold text-slate-600 uppercase tracking-widest">Type</div>
          <div className="col-span-1 text-[10px] font-bold text-slate-600 uppercase tracking-widest text-right">Dls</div>
          <div className="col-span-2 text-[10px] font-bold text-slate-600 uppercase tracking-widest text-right">Actions</div>
        </div>
        
        <div className="divide-y divide-slate-200">
          {(isLoading || tenantLoading) && (
             <div className="col-span-12 p-8 text-center text-slate-500 text-xs font-bold uppercase tracking-widest">
               Syncing Vault...
             </div>
          )}
          {!(isLoading || tenantLoading) && filteredAssets.map((asset) => (
            <div key={asset.id} className="px-4 py-4 grid grid-cols-12 gap-4 items-center hover:bg-slate-50 transition-colors">
              <div className="col-span-5 flex items-center gap-3">
                <i className={`fa-solid ${asset.icon} ${asset.color} text-lg w-6 text-center`}></i>
                <div>
                  <p className="text-sm font-bold text-slate-900 leading-tight cursor-pointer hover:text-emerald-600" onClick={() => handleAssetInteraction(asset)}>{asset.title}</p>
                  <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">Updated {asset.updated}</p>
                </div>
              </div>
              <div className="col-span-2">
                <span className="bg-slate-100 border border-slate-200 text-slate-600 text-[9px] font-bold px-2 py-0.5 uppercase tracking-widest">
                  {asset.category}
                </span>
              </div>
              <div className="col-span-2 text-xs font-bold text-slate-600">
                {asset.type}
              </div>
              <div className="col-span-1 text-right">
                <span className="text-sm font-black text-slate-800">{asset.downloads || 0}</span>
              </div>
              <div className="col-span-2 flex justify-end gap-4">
                <button onClick={() => openEditModal(asset)} className="text-slate-400 hover:text-blue-600 transition-colors text-sm" title="Edit Asset">
                  <i className="fa-solid fa-pen-to-square"></i>
                </button>
                <button onClick={() => handleDelete(asset.id, asset.storagePath)} className="text-slate-400 hover:text-red-600 transition-colors text-sm" title="Delete Asset">
                  <i className="fa-solid fa-trash-can"></i>
                </button>
              </div>
            </div>
          ))}
          {!(isLoading || tenantLoading) && filteredAssets.length === 0 && (
            <div className="col-span-full py-10 text-center text-slate-500 text-sm font-bold uppercase tracking-widest">
              No assets found.
            </div>
          )}
        </div>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/50 backdrop-blur-sm animate-in fade-in">
          <div className="w-full max-w-md bg-white h-full border-l border-slate-300 shadow-2xl flex flex-col slide-in-from-right duration-300">
            
            <div className="bg-slate-900 p-6 flex justify-between items-center">
              <div>
                <h2 className="text-lg font-black text-white uppercase tracking-tight">
                  {editingAsset ? 'Edit Asset' : 'Upload New Asset'}
                </h2>
                <p className="text-[9px] font-bold text-emerald-500 uppercase tracking-widest mt-1">Resource Vault Management</p>
              </div>
              <button onClick={() => setIsModalOpen(false)} className="text-slate-400 hover:text-white transition-colors">
                <i className="fa-solid fa-xmark text-xl"></i>
              </button>
            </div>

            <form onSubmit={handleModalSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">
              <div>
                <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-2">Asset Title</label>
                <input 
                  required 
                  type="text" 
                  value={formData.title} 
                  onChange={(e) => setFormData({...formData, title: e.target.value})} 
                  className="w-full bg-slate-50 border border-slate-300 px-4 py-2.5 text-sm font-bold text-slate-800 focus:outline-none focus:border-emerald-500" 
                  placeholder="e.g., Q1 Pitch Deck" 
                />
              </div>

              <div>
                <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-2">Primary Category</label>
                <select 
                  required 
                  value={formData.category} 
                  onChange={(e) => setFormData({...formData, category: e.target.value})} 
                  className="w-full bg-slate-50 border border-slate-300 px-4 py-2.5 text-sm font-bold text-slate-800 focus:outline-none focus:border-emerald-500 cursor-pointer"
                >
                  {categories.filter(c => c !== 'All').map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-2">Asset Format / Type</label>
                <div className="grid grid-cols-2 gap-3">
                  {['PDF', 'Sheet', 'Interactive', 'Video'].map(type => (
                    <label key={type} className={`border p-3 flex items-center gap-2 cursor-pointer transition-colors ${formData.type === type ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'}`}>
                      <input type="radio" name="type" value={type} checked={formData.type === type} onChange={(e) => setFormData({...formData, type: e.target.value})} className="hidden" />
                      <i className={`fa-solid ${type === 'PDF' ? 'fa-file-pdf' : type === 'Sheet' ? 'fa-file-excel' : type === 'Interactive' ? 'fa-calculator' : 'fa-circle-play'}`}></i>
                      <span className="text-xs font-bold uppercase">{type}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="pt-4">
                <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-2">
                  {formData.type === 'Interactive' || formData.type === 'Video' ? 'Target URL' : 'File Upload'}
                </label>
                
                {formData.type === 'Interactive' || formData.type === 'Video' ? (
                  <input type="url" value={formData.targetUrl} onChange={(e) => setFormData({...formData, targetUrl: e.target.value})} placeholder="https://" className="w-full bg-slate-50 border border-slate-300 px-4 py-2.5 text-sm font-bold text-slate-800 focus:outline-none focus:border-emerald-500" required={!editingAsset} />
                ) : (
                  <div 
                    onClick={() => fileInputRef.current?.click()}
                    className="border-2 border-dashed border-slate-300 bg-slate-50 p-6 flex flex-col items-center justify-center text-center cursor-pointer hover:bg-slate-100 hover:border-emerald-400 transition-colors"
                  >
                    <input 
                      type="file" 
                      ref={fileInputRef} 
                      onChange={handleFileSelect} 
                      className="hidden" 
                      accept={formData.type === 'PDF' ? '.pdf' : '.xls,.xlsx,.csv'} 
                    />
                    <i className="fa-solid fa-cloud-arrow-up text-2xl text-slate-400 mb-2"></i>
                    <span className="text-xs font-bold text-slate-600">
                      {selectedFile ? selectedFile.name : (editingAsset?.fileUrl ? 'Click to replace existing file' : 'Click to browse or drag file here')}
                    </span>
                    <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-1">Max size 25MB</span>
                  </div>
                )}
              </div>
            </form>

            <div className="p-6 border-t border-slate-200 bg-slate-50 flex gap-3">
              <button onClick={() => setIsModalOpen(false)} disabled={isSubmitting} className="flex-1 bg-white border border-slate-300 text-slate-700 text-[10px] font-bold py-3 uppercase tracking-widest transition-colors hover:bg-slate-100 disabled:opacity-50">
                Cancel
              </button>
              <button onClick={handleModalSubmit} disabled={isSubmitting || (!editingAsset && !selectedFile && formData.type !== 'Interactive' && formData.type !== 'Video')} className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] font-bold py-3 uppercase tracking-widest transition-colors shadow-sm disabled:opacity-50">
                {isSubmitting ? uploadProgressText : (editingAsset ? 'Save Changes' : 'Publish Asset')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}